import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import FocusLock from "react-focus-lock";
import { Download, ExternalLink, ShieldCheck, Loader2, AlertCircle, CreditCard, XCircle, RefreshCcw, ArrowRight, X, Maximize2, PlayCircle, Award, GraduationCap } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Reveal from "@/components/Reveal";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

async function downloadFile(productId, filename) {
  const res = await fetch(`${API}/downloads/${productId}`, { credentials: "include" });
  if (!res.ok) {
    let msg = "Download failed";
    try { const j = await res.json(); if (j?.detail) msg = j.detail; } catch {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `${productId}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [subActionLoading, setSubActionLoading] = useState(null);
  const [error, setError] = useState("");
  const [access, setAccess] = useState(null);
  const [courseModal, setCourseModal] = useState(null); // { productId, name, initialHash }
  const [progress, setProgress] = useState({}); // { product_id: {slide_hash, updated_at, product_name} }
  const [lmsCourses, setLmsCourses] = useState([]);

  const loadAll = async () => {
    try {
      const [{ data: purch }, { data: sub }, { data: acc }, { data: prog }, { data: courses }] = await Promise.all([
        api.get("/me/purchases"),
        api.get("/me/subscription"),
        api.get("/me/access"),
        api.get("/me/course-progress"),
        api.get("/me/lms-courses"),
      ]);
      setPurchases(purch);
      setSubscription(sub);
      setAccess(acc);
      setProgress(prog || {});
      setLmsCourses(courses || []);
    } catch {
      /* handled per section */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user !== false) loadAll();
  }, [user]);

  if (user === null) {
    return (
      <SiteLayout branch="main">
        <div className="section text-center text-[var(--muted)]">Loading…</div>
      </SiteLayout>
    );
  }
  if (user === false) return <Navigate to="/login?redirect=/dashboard" replace />;

  const handleDownload = async (p) => {
    setDownloading(p.session_id);
    setError("");
    try {
      await downloadFile(p.product_id, `${p.product_name}.pdf`);
    } catch (e) {
      setError(e.message || "Download failed");
    } finally {
      setDownloading(null);
    }
  };

  const openPortal = async () => {
    setSubActionLoading("portal");
    setError("");
    try {
      const { data } = await api.post("/me/subscription/portal", {
        return_url: window.location.origin + "/dashboard",
      });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiError(e));
      setSubActionLoading(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel at the end of your current billing period? You'll keep access until then.")) return;
    setSubActionLoading("cancel");
    setError("");
    try {
      await api.post("/me/subscription/cancel");
      await loadAll();
      await refresh();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubActionLoading(null);
    }
  };

  const resume = async () => {
    setSubActionLoading("resume");
    setError("");
    try {
      await api.post("/me/subscription/resume");
      await loadAll();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubActionLoading(null);
    }
  };

  const stripeSub = subscription?.stripe;
  const membershipActive = user.membership_status === "active";
  const expiry = user.membership_expires_at ? new Date(user.membership_expires_at) : null;
  const expiryOk = expiry && expiry > new Date();
  const cancelPending = stripeSub?.cancel_at_period_end;

  return (
    <SiteLayout branch="main">
      <section className="section bg-[var(--light)]">
        <Reveal className="max-w-[1200px] mx-auto">
          <span className="eyebrow-dark">MEMBER PORTAL</span>
          <h1 className="font-display text-[clamp(36px,4vw,52px)] leading-[1.05] text-[var(--navy)] mt-3">
            Welcome back, {user.name?.split(" ")[0] || "member"}.
          </h1>
          {user.role === "admin" && (
            <Link to="/admin" className="btn btn-navy mt-6 inline-flex" data-testid="admin-shortcut">
              Open Admin Console
            </Link>
          )}
          {access?.continuum && (
            <Link
              to="/portal/continuum"
              className="btn btn-gold mt-6 ml-2 inline-flex"
              data-testid="continuum-portal-shortcut"
            >
              Enter Continuum Portal <ArrowRight className="ml-2" size={14} />
            </Link>
          )}
          {stripeSub && (
            <button
              onClick={openPortal}
              disabled={subActionLoading === "portal"}
              className="btn mt-6 ml-2 inline-flex items-center gap-2 border border-[var(--navy)] bg-white text-[var(--navy)] hover:bg-[var(--navy)] hover:text-white transition-colors disabled:opacity-50"
              data-testid="header-billing-portal-btn"
              title="Update payment method, view invoices, or cancel"
            >
              {subActionLoading === "portal" ? (
                <><Loader2 className="animate-spin" size={14} /> Opening…</>
              ) : (
                <><CreditCard size={14} /> Manage Billing</>
              )}
            </button>
          )}
        </Reveal>

        {(() => {
          // Compute most recent course-progress entry for "Continue where you left off"
          const entries = Object.entries(progress || {});
          if (!entries.length) return null;
          entries.sort(([, a], [, b]) => (b.updated_at || "").localeCompare(a.updated_at || ""));
          const [pid, entry] = entries[0];
          // Only surface if the user still owns the product
          const owned = purchases.some((p) => p.product_id === pid && p.product_type === "course");
          if (!owned) return null;
          return (
            <Reveal className="max-w-[1200px] mx-auto mt-8">
              <button
                onClick={() => setCourseModal({ productId: pid, name: entry.product_name, initialHash: entry.slide_hash || "" })}
                className="w-full bg-[var(--navy)] text-white p-6 border border-[var(--navy)] hover:bg-[#0a1730] transition-colors text-left flex items-center justify-between group"
                data-testid="continue-course-tile"
              >
                <div>
                  <div className="text-[10px] tracking-widest text-[var(--gold)] font-bold">CONTINUE WHERE YOU LEFT OFF</div>
                  <div className="font-display text-xl mt-1">{entry.product_name}</div>
                  <div className="text-xs text-neutral-400 mt-1">
                    Last opened {new Date(entry.updated_at).toLocaleString()}
                  </div>
                </div>
                <ArrowRight className="text-[var(--gold)] group-hover:translate-x-1 transition-transform" size={20} />
              </button>
            </Reveal>
          );
        })()}

        {/* CONTINUE LEARNING — Native LMS courses */}
        {!loading && lmsCourses.length > 0 && (
          <Reveal className="max-w-[1200px] mx-auto mt-10">
            <div className="bg-white border border-[var(--line)] p-8" data-testid="continue-learning">
              <div className="flex items-center gap-2.5 mb-6">
                <GraduationCap size={18} className="text-[var(--gold)]" />
                <h2 className="font-display text-2xl text-[var(--navy)]">Continue Learning</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                {lmsCourses.map((c) => (
                  <CourseTile key={c.product_id} course={c} />
                ))}
              </div>
            </div>
          </Reveal>
        )}

        <div className="grid lg:grid-cols-[1.4fr_0.6fr] gap-6 max-w-[1200px] mx-auto mt-10">
          {/* PURCHASES */}
          <Reveal>
            <div className="bg-white border border-[var(--line)] p-8">
              <h2 className="font-display text-2xl text-[var(--navy)] mb-6">Your library</h2>
              {loading ? (
                <div className="text-[var(--muted)] text-sm">Loading purchases…</div>
              ) : purchases.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-[var(--muted)] mb-6">You haven't made any purchases yet.</div>
                  <Link to="/academy" className="btn btn-navy inline-flex">Browse the Academy</Link>
                </div>
              ) : (
                <ul className="divide-y divide-[var(--line)]" data-testid="purchases-list">
                  {purchases.map((p) => {
                    const isDownloadable = ["download", "toolkit"].includes(p.product_type);
                    return (
                      <li key={p.session_id} className="flex items-center justify-between py-4">
                        <div>
                          <div className="text-[10px] tracking-widest text-[var(--gold)] font-bold">
                            {p.product_type.toUpperCase()}
                          </div>
                          <div className="font-display text-lg text-[var(--navy)]">{p.product_name}</div>
                          <div className="text-xs text-[var(--muted)] mt-1">
                            ${p.amount.toFixed(2)} · {new Date(p.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        {isDownloadable ? (
                          <button
                            onClick={() => handleDownload(p)}
                            disabled={downloading === p.session_id}
                            className="text-[var(--gold)] font-bold text-xs flex items-center gap-2 hover:gap-3 transition-all disabled:opacity-50"
                            data-testid={`download-${p.product_id}`}
                          >
                            {downloading === p.session_id ? (
                              <><Loader2 className="animate-spin" size={14} /> Fetching…</>
                            ) : (
                              <><Download size={14} /> Download</>
                            )}
                          </button>
                        ) : p.product_type === "course" ? (
                          <button
                            onClick={() => setCourseModal({
                              productId: p.product_id,
                              name: p.product_name,
                              initialHash: progress[p.product_id]?.slide_hash || "",
                            })}
                            className="text-[var(--gold)] font-bold text-xs flex items-center gap-2 hover:gap-3 transition-all"
                            data-testid={`launch-course-${p.product_id}`}
                          >
                            <ExternalLink size={14} />
                            {progress[p.product_id]?.slide_hash ? "Resume course" : "Launch course"}
                          </button>
                        ) : (
                          <span className="text-[var(--gold)] font-bold text-xs flex items-center gap-2" data-testid={`open-${p.product_id}`}>
                            <ExternalLink size={14} /> {p.product_type === "membership" ? "Active" : "Open"}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {error && <p className="text-red-700 text-xs mt-4" data-testid="dashboard-error">{error}</p>}
            </div>
          </Reveal>

          {/* SUBSCRIPTION */}
          <Reveal>
            <SubscriptionCard
              active={membershipActive && expiryOk}
              expiry={expiry}
              stripeSub={stripeSub}
              cancelPending={cancelPending}
              subActionLoading={subActionLoading}
              onPortal={openPortal}
              onCancel={cancel}
              onResume={resume}
            />
          </Reveal>
        </div>
      </section>
      {courseModal && (
        <CourseModal
          productId={courseModal.productId}
          name={courseModal.name}
          initialHash={courseModal.initialHash || ""}
          onClose={() => setCourseModal(null)}
          onProgress={(entry) => setProgress((prev) => ({ ...prev, [courseModal.productId]: entry }))}
        />
      )}
    </SiteLayout>
  );
}

function CourseModal({ productId, name, initialHash, onClose, onProgress }) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const baseSrc = `${backendUrl}/api/courses/${productId}/index.html`;
  const iframeSrc = initialHash ? `${baseSrc}${initialHash}` : baseSrc;
  const newTabUrl = `/course/${productId}${initialHash || ""}`;
  const lastSavedRef = useRef(initialHash || "");
  const iframeRef = useRef(null);
  const crossOriginWarnedRef = useRef(false); // one-shot rate limit

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // Poll the iframe's current hash every 3s. Rise/Articulate courses update
  // window.location.hash as the learner navigates lessons. When it changes
  // (debounced by comparing to lastSavedRef), persist to /api/me/course-progress.
  useEffect(() => {
    const save = async (hash) => {
      if (hash === lastSavedRef.current) return;
      lastSavedRef.current = hash;
      try {
        const { data } = await api.post("/me/course-progress", {
          product_id: productId, slide_hash: hash,
        });
        if (data?.progress && onProgress) onProgress(data.progress);
      } catch { /* silent — best effort */ }
    };

    const id = setInterval(() => {
      const el = iframeRef.current;
      if (!el || !el.contentWindow) return;
      try {
        const h = el.contentWindow.location.hash || "";
        if (h) save(h);
      } catch (e) {
        // Same-origin is expected because the iframe src is on
        // REACT_APP_BACKEND_URL which shares the app host. If that changes
        // (e.g. moving assets to a CDN subdomain), warn once so it's
        // diagnosable — otherwise every 3s interval would spam the console.
        if (!crossOriginWarnedRef.current) {
          crossOriginWarnedRef.current = true;
          console.warn(
            "[CourseModal] cannot read iframe hash — course served cross-origin, resume progress disabled.",
            e?.name || e,
          );
        }
      }
    }, 3000);
    return () => clearInterval(id);
  }, [productId, onProgress]);

  return (
    <FocusLock returnFocus>
      <div
        className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex flex-col"
        data-testid="course-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Course viewer: ${name}`}
      >
        <div className="flex items-center justify-between px-4 py-2 bg-[var(--navy)] text-white text-sm">
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 hover:text-[var(--gold)] transition-colors"
            data-testid="course-modal-close"
          >
            <X size={14} /> Close
          </button>
          <span className="font-medium truncate max-w-[50vw]">{name}</span>
          <a
            href={newTabUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 hover:text-[var(--gold)] transition-colors"
            data-testid="course-modal-newtab"
          >
            <Maximize2 size={14} /> Open in new tab
          </a>
        </div>
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          title={name || "Course"}
          className="flex-1 w-full border-0 bg-white"
          allow="autoplay; fullscreen; encrypted-media"
          data-testid="course-modal-iframe"
        />
      </div>
    </FocusLock>
  );
}

function CourseTile({ course }) {
  const [downloading, setDownloading] = useState(false);

  const downloadCert = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/lms/courses/${course.product_id}/certificate`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `catalyst-certificate-${course.product_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { /* silent — user can retry */ }
    finally { setDownloading(false); }
  };

  const started = course.completed_lessons > 0;
  const done = course.certificate_ready;

  return (
    <article
      className="border border-[var(--line)] p-5 hover:border-[var(--gold)] transition-colors bg-[var(--cream)]/40"
      data-testid={`course-tile-${course.product_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-display text-lg text-[var(--navy)] leading-tight">{course.name}</h3>
        {done && (
          <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 text-[9px] font-bold tracking-widest flex-shrink-0">
            <Award size={9} /> COMPLETE
          </span>
        )}
      </div>
      <div className="mt-4">
        <div className="flex items-center justify-between text-[10px] text-[var(--muted)] mb-1.5 tracking-wider uppercase">
          <span className="font-bold">Progress</span>
          <span>{course.completed_lessons} / {course.total_lessons} lessons</span>
        </div>
        <div className="h-1.5 bg-[var(--line)] overflow-hidden">
          <div
            className="h-full bg-[var(--gold)] transition-all"
            style={{ width: `${course.percent}%` }}
            data-testid={`course-progress-${course.product_id}`}
          />
        </div>
        <div className="text-[10px] text-[var(--muted)] mt-1 tracking-wider">{course.percent}% complete</div>
      </div>
      <div className="flex flex-wrap gap-2 mt-5">
        <Link
          to={`/academy/${course.product_id}/learn`}
          className="btn btn-navy !py-2 !px-3 !text-[11px]"
          data-testid={`course-resume-${course.product_id}`}
        >
          <PlayCircle size={11} className="mr-1.5" /> {started ? (done ? "Review" : "Resume") : "Start"}
        </Link>
        {done && (
          <button
            onClick={downloadCert}
            disabled={downloading}
            className="btn btn-gold !py-2 !px-3 !text-[11px] disabled:opacity-60"
            data-testid={`course-certificate-${course.product_id}`}
          >
            {downloading ? <Loader2 className="animate-spin mr-1.5" size={11} /> : <Award size={11} className="mr-1.5" />}
            Certificate
          </button>
        )}
      </div>
    </article>
  );
}

function SubscriptionCard({ active, expiry, stripeSub, cancelPending, subActionLoading, onPortal, onCancel, onResume }) {
  const periodEnd = stripeSub?.current_period_end
    ? new Date(stripeSub.current_period_end * 1000)
    : null;

  return (
    <div
      className={`p-8 border ${active ? "bg-[var(--navy)] text-white border-[var(--navy)]" : "bg-white border-[var(--line)]"}`}
      data-testid="subscription-card"
    >
      <ShieldCheck className={active ? "text-[var(--gold)]" : "text-[var(--muted)]"} size={28} />
      <h3 className="font-display text-2xl mt-4">Partner Portal</h3>

      {!active && (
        <>
          <div className="text-[var(--muted)] text-[10px] tracking-widest mt-2 font-bold">INACTIVE</div>
          <p className="text-sm text-[var(--muted)] mt-3">
            Unlock the full template library, monthly office hours, and priority access.
          </p>
          <Link to="/academy?category=Memberships" className="btn btn-gold w-full mt-6 justify-center" data-testid="upgrade-cta">
            View Memberships
          </Link>
        </>
      )}

      {active && (
        <>
          <div className="text-[var(--gold)] text-[10px] tracking-widest mt-2 font-bold">
            {cancelPending ? "ACTIVE · CANCELING" : "ACTIVE"}
          </div>
          <p className="text-sm text-neutral-300 mt-3">
            {cancelPending ? "Access ends" : "Renews / expires"} on{" "}
            <strong className="text-white">
              {(periodEnd || expiry).toLocaleDateString()}
            </strong>
            .
          </p>

          {cancelPending && (
            <div className="mt-4 p-3 bg-[#8a3e1f] border border-[#c78968] text-xs flex items-start gap-2">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              <span>Your subscription will not renew. You can resume anytime before {(periodEnd || expiry).toLocaleDateString()}.</span>
            </div>
          )}

          <div className="mt-6 space-y-2">
            {stripeSub && (
              <button
                onClick={onPortal}
                disabled={!!subActionLoading}
                className="w-full flex items-center justify-center gap-2 py-3 border border-[var(--gold)] text-[var(--gold)] text-[11px] font-bold tracking-widest hover:bg-[var(--gold)] hover:text-[var(--navy)] transition-colors disabled:opacity-50"
                data-testid="sub-portal-btn"
              >
                {subActionLoading === "portal" ? (
                  <><Loader2 className="animate-spin" size={14} /> OPENING…</>
                ) : (
                  <><CreditCard size={14} /> MANAGE BILLING</>
                )}
              </button>
            )}

            {!cancelPending && stripeSub && (
              <button
                onClick={onCancel}
                disabled={!!subActionLoading}
                className="w-full flex items-center justify-center gap-2 py-3 text-neutral-400 text-[11px] font-bold tracking-widest hover:text-white transition-colors disabled:opacity-50"
                data-testid="sub-cancel-btn"
              >
                {subActionLoading === "cancel" ? (
                  <><Loader2 className="animate-spin" size={14} /> CANCELING…</>
                ) : (
                  <><XCircle size={14} /> CANCEL SUBSCRIPTION</>
                )}
              </button>
            )}

            {cancelPending && stripeSub && (
              <button
                onClick={onResume}
                disabled={!!subActionLoading}
                className="w-full flex items-center justify-center gap-2 py-3 bg-[var(--gold)] text-[var(--navy)] text-[11px] font-bold tracking-widest hover:bg-[var(--gold-2)] transition-colors disabled:opacity-50"
                data-testid="sub-resume-btn"
              >
                {subActionLoading === "resume" ? (
                  <><Loader2 className="animate-spin" size={14} /> RESUMING…</>
                ) : (
                  <><RefreshCcw size={14} /> RESUME SUBSCRIPTION</>
                )}
              </button>
            )}

            {!stripeSub && active && (
              <p className="text-xs text-neutral-400 leading-relaxed pt-2 border-t border-[var(--line-dark)]">
                Period-based membership. Renews when you re-purchase.
                <Link to="/academy?category=Memberships" className="block text-[var(--gold)] font-bold mt-2">
                  Extend membership →
                </Link>
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
